const BOUNDARY_OVERLAY_STYLE = {
  stroke: '#2563eb',
  'stroke-width': 2,
  'stroke-opacity': 1,
  fill: '#2563eb',
  'fill-opacity': 0.18,
}

/** Mapbox Static Images GET URLs must be ≤ 8192 characters. */
const MAPBOX_STATIC_URL_MAX_LENGTH = 8192
const COORD_PRECISION = 5
const SIMPLIFY_TOLERANCES = [0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1]

const roundCoord = (value) => Number(Number(value).toFixed(COORD_PRECISION))

const roundPosition = (position) => {
  if (!Array.isArray(position) || position.length < 2) return position
  return [roundCoord(position[0]), roundCoord(position[1])]
}

const perpendicularDistance = (point, start, end) => {
  const [x, y] = point
  const [x1, y1] = start
  const [x2, y2] = end
  const dx = x2 - x1
  const dy = y2 - y1

  if (dx === 0 && dy === 0) {
    return Math.hypot(x - x1, y - y1)
  }

  const t = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy)))
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy))
}

/** Douglas–Peucker line simplification for a ring or line of [lng, lat] positions. */
const simplifyLine = (points, tolerance) => {
  if (!Array.isArray(points) || points.length < 3) return points

  const find = (segment) => {
    if (segment.length < 3) return segment

    let maxDistance = 0
    let index = 0
    const start = segment[0]
    const end = segment[segment.length - 1]

    for (let i = 1; i < segment.length - 1; i += 1) {
      const distance = perpendicularDistance(segment[i], start, end)
      if (distance > maxDistance) {
        maxDistance = distance
        index = i
      }
    }

    if (maxDistance > tolerance) {
      const left = find(segment.slice(0, index + 1))
      const right = find(segment.slice(index))
      return left.slice(0, -1).concat(right)
    }

    return [start, end]
  }

  return find(points)
}

const closeRing = (ring) => {
  if (!Array.isArray(ring) || ring.length === 0) return ring
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] === last[0] && first[1] === last[1]) return ring
  return [...ring, [...first]]
}

const ringBounds = (ring) => {
  if (!Array.isArray(ring)) return null

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity

  ring.forEach((position) => {
    if (!Array.isArray(position) || position.length < 2) return
    const x = Number(position[0])
    const y = Number(position[1])
    if (!Number.isFinite(x) || !Number.isFinite(y)) return
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  })

  if (!Number.isFinite(minX)) return null
  return { minX, maxX, minY, maxY, area: (maxX - minX) * (maxY - minY) }
}

/** Bounding-box area for geometry comparison (0 for points / unknown shapes). */
const geometryBoundsArea = (geometry) => {
  if (!geometry || typeof geometry !== 'object') return 0

  switch (geometry.type) {
    case 'Polygon':
      return ringBounds(geometry.coordinates?.[0])?.area ?? 0
    case 'MultiPolygon': {
      let best = 0
      ;(geometry.coordinates || []).forEach((polygon) => {
        const area = ringBounds(polygon?.[0])?.area ?? 0
        if (area > best) best = area
      })
      return best
    }
    case 'LineString':
      return ringBounds(geometry.coordinates)?.area ?? 0
    case 'MultiLineString': {
      let best = 0
      ;(geometry.coordinates || []).forEach((line) => {
        const area = ringBounds(line)?.area ?? 0
        if (area > best) best = area
      })
      return best
    }
    case 'Point':
    case 'MultiPoint':
    default:
      return 0
  }
}

/** Compact placeholder for rings that collapse under simplification. */
const ringToBoundingBox = (ring) => {
  const bounds = ringBounds(ring)
  if (!bounds) return closeRing(ring.map(roundPosition))

  const { minX, maxX, minY, maxY } = bounds
  return [
    roundPosition([minX, minY]),
    roundPosition([maxX, minY]),
    roundPosition([maxX, maxY]),
    roundPosition([minX, maxY]),
    roundPosition([minX, minY]),
  ]
}

const simplifyRing = (ring, tolerance) => {
  if (!Array.isArray(ring) || ring.length < 4) return ring

  const rounded = ring.map(roundPosition)
  const simplified = simplifyLine(rounded, tolerance)
  const closed = closeRing(simplified)

  // A valid polygon ring needs at least 4 positions (3 + closing point).
  if (closed.length >= 4) return closed
  return ringToBoundingBox(rounded)
}

/**
 * Thumbnail overlays only need a coarse city outline: keep the largest
 * exterior ring, drop holes/islands, and simplify aggressively.
 */
const toThumbnailGeometry = (geometry) => {
  if (!geometry || typeof geometry !== 'object') return null

  if (geometry.type === 'GeometryCollection') {
    const parts = (geometry.geometries || [])
      .map(toThumbnailGeometry)
      .filter(Boolean)
    return parts[0] || null
  }

  if (geometry.type === 'Polygon') {
    const exterior = geometry.coordinates?.[0]
    if (!exterior) return null
    return { type: 'Polygon', coordinates: [exterior] }
  }

  if (geometry.type === 'MultiPolygon') {
    let bestRing = null
    let bestArea = -1

    ;(geometry.coordinates || []).forEach((polygon) => {
      const exterior = polygon?.[0]
      if (!exterior) return
      const bounds = ringBounds(exterior)
      const area = bounds?.area ?? 0
      if (area > bestArea) {
        bestArea = area
        bestRing = exterior
      }
    })

    if (!bestRing) return null
    return { type: 'Polygon', coordinates: [bestRing] }
  }

  return geometry
}

const simplifyGeometry = (geometry, tolerance) => {
  const thumbnail = toThumbnailGeometry(geometry)
  if (!thumbnail) return null

  if (thumbnail.type === 'Polygon') {
    return {
      type: 'Polygon',
      coordinates: [simplifyRing(thumbnail.coordinates[0], tolerance)],
    }
  }

  if (thumbnail.type === 'LineString') {
    return {
      type: 'LineString',
      coordinates: simplifyLine(thumbnail.coordinates.map(roundPosition), tolerance),
    }
  }

  if (thumbnail.type === 'MultiLineString') {
    return {
      type: 'MultiLineString',
      coordinates: thumbnail.coordinates.map((line) =>
        simplifyLine(line.map(roundPosition), tolerance)
      ),
    }
  }

  if (thumbnail.type === 'Point') {
    return { type: 'Point', coordinates: roundPosition(thumbnail.coordinates) }
  }

  if (thumbnail.type === 'MultiPoint') {
    return {
      type: 'MultiPoint',
      coordinates: thumbnail.coordinates.map(roundPosition),
    }
  }

  return thumbnail
}

const toMapboxOverlayGeoJson = (boundaryGeoJson) => {
  if (!boundaryGeoJson) return null

  if (boundaryGeoJson.type === 'Feature') {
    const geometry = toThumbnailGeometry(boundaryGeoJson.geometry)
    if (!geometry) return null
    return {
      type: 'Feature',
      properties: { ...BOUNDARY_OVERLAY_STYLE },
      geometry,
    }
  }

  if (boundaryGeoJson.type === 'FeatureCollection') {
    const features = (boundaryGeoJson.features || [])
      .map((feature) => {
        const geometry = toThumbnailGeometry(feature.geometry)
        if (!geometry) return null
        return {
          type: 'Feature',
          properties: { ...BOUNDARY_OVERLAY_STYLE },
          geometry,
        }
      })
      .filter(Boolean)

    // Prefer a single largest feature for Static Images URL budget.
    if (features.length === 0) return null
    if (features.length === 1) return features[0]

    let best = features[0]
    let bestArea = -1
    features.forEach((feature) => {
      const area = geometryBoundsArea(feature.geometry)
      if (area > bestArea) {
        bestArea = area
        best = feature
      }
    })
    return best
  }

  const geometry = toThumbnailGeometry(boundaryGeoJson)
  if (!geometry) return null
  return {
    type: 'Feature',
    properties: { ...BOUNDARY_OVERLAY_STYLE },
    geometry,
  }
}

const simplifyOverlayGeoJson = (overlayGeoJson, tolerance) => {
  if (!overlayGeoJson?.geometry) return null
  const geometry = simplifyGeometry(overlayGeoJson.geometry, tolerance)
  if (!geometry) return null
  return {
    type: 'Feature',
    properties: { ...BOUNDARY_OVERLAY_STYLE },
    geometry,
  }
}

const buildStaticUrl = ({ overlays, viewport, width, height, token }) =>
  `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/` +
  `${overlays.join(',')}/${viewport}/${width}x${height}@2x` +
  `?access_token=${encodeURIComponent(token)}`

/** Build a Mapbox Static Images API URL for a location thumbnail. */
export const buildMapboxStaticImageUrl = (
  longitude,
  latitude,
  { width = 320, height = 200, zoom = 13, boundaryGeoJson = null } = {},
) => {
  const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
  const hasCoordinates = latitude != null && longitude != null
  const styledBoundary = toMapboxOverlayGeoJson(boundaryGeoJson)

  if (!token || (!hasCoordinates && !styledBoundary)) return null

  const pinOverlay = hasCoordinates ? `pin-s+2563eb(${longitude},${latitude})` : null
  const coordinateViewport = hasCoordinates ? `${longitude},${latitude},${zoom},0` : null

  const tryBuild = (overlayGeoJson) => {
    const overlays = []
    if (pinOverlay) overlays.push(pinOverlay)

    if (overlayGeoJson) {
      overlays.push(`geojson(${encodeURIComponent(JSON.stringify(overlayGeoJson))})`)
    }

    if (overlays.length === 0) return null

    const viewport = overlayGeoJson ? 'auto' : coordinateViewport
    if (!viewport) return null

    return buildStaticUrl({ overlays, viewport, width, height, token })
  }

  if (styledBoundary) {
    for (const tolerance of SIMPLIFY_TOLERANCES) {
      const simplified = simplifyOverlayGeoJson(styledBoundary, tolerance)
      const url = tryBuild(simplified)
      if (url && url.length <= MAPBOX_STATIC_URL_MAX_LENGTH) {
        return url
      }
    }
  }

  // Pin-only fallback when the boundary overlay cannot fit the URL limit.
  return tryBuild(null)
}

export const buildGoogleMapsCoordinatesUrl = (latitude, longitude) =>
  `https://www.google.com/maps?q=${latitude},${longitude}`

export const buildGooglePlacesUrl = (placeId) =>
  `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`
